import { useCallback } from "react";
import { Modal, Stack, Text, Table } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { RecoverSpecimenRow } from "./RecoverSpecimenRow";

interface ArchivedSpecimen {
  id: string;
  number: string;
  source: string | null;
  material: string | null;
  archivedAt: string | null;
}

interface RecoverMutationLike {
  mutate: (
    variables: { caseId: string; specimenId: string },
    options?: { onSuccess?: () => void; onError?: () => void }
  ) => void;
  isPending: boolean;
  variables?: { caseId: string; specimenId: string };
}

interface RecoverSpecimensModalProps {
  opened: boolean;
  onClose: () => void;
  caseId: string;
  archivedSpecimens: ArchivedSpecimen[];
  // The workflow-specific recover mutation (grossing or pathologist review).
  recoverMutation: RecoverMutationLike;
}

export function RecoverSpecimensModal({
  opened,
  onClose,
  caseId,
  archivedSpecimens,
  recoverMutation,
}: RecoverSpecimensModalProps) {
  const handleRecover = useCallback(
    (specimenId: string, number: string) => {
      recoverMutation.mutate(
        { caseId, specimenId },
        {
          onSuccess: () => {
            notifications.show({
              color: "green",
              title: "Specimen recovered",
              message: `Specimen ${number} was restored to the case.`,
            });
            // This was the last archived specimen — nothing left to recover.
            if (archivedSpecimens.length <= 1) {
              onClose();
            }
          },
          onError: () => {
            notifications.show({
              color: "red",
              title: "Couldn't recover specimen",
              message:
                "Something went wrong restoring the specimen. Please try again.",
            });
          },
        }
      );
    },
    [recoverMutation, caseId, archivedSpecimens.length, onClose]
  );

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Recover removed specimens"
      centered
      size="xl"
    >
      <Stack gap="md">
        <Text size="sm" c="dimmed">
          Removed specimens are hidden from the report and the grid but are not
          deleted. Restore one to bring it — and its blocks, slides and
          diagnosis — back into the case.
        </Text>

        {archivedSpecimens.length === 0 ? (
          <Text size="sm">No removed specimens to recover.</Text>
        ) : (
          <Table verticalSpacing="xs" highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Specimen</Table.Th>
                <Table.Th>Source</Table.Th>
                <Table.Th>Material</Table.Th>
                <Table.Th />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {archivedSpecimens.map((specimen) => (
                <RecoverSpecimenRow
                  key={specimen.id}
                  id={specimen.id}
                  number={specimen.number}
                  source={specimen.source}
                  material={specimen.material}
                  isLoading={
                    recoverMutation.isPending &&
                    recoverMutation.variables?.specimenId === specimen.id
                  }
                  disabled={recoverMutation.isPending}
                  onRecover={handleRecover}
                />
              ))}
            </Table.Tbody>
          </Table>
        )}
      </Stack>
    </Modal>
  );
}
